import "core-js/modules/es6.array.map";
import _defineProperty from "/opt/build/repo/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty";
import "core-js/modules/es6.array.from";
import "core-js/modules/es6.array.sort";
import "core-js/modules/es6.array.for-each";
import "core-js/modules/web.dom.iterable";
import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.string.iterator";
import "core-js/modules/es6.set";
import _slicedToArray from "/opt/build/repo/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _extends from '@babel/runtime/helpers/esm/extends';
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSprings, animated } from 'react-spring';
import { useDrag } from 'react-use-gesture';
import useResizeObserver from 'use-resize-observer';
import 'intersection-observer';

var slidesWrapperStyle = function slidesWrapperStyle(vertical) {
  return {
    display: 'flex',
    height: '100%',
    width: '100%',
    flexWrap: 'nowrap',
    alignItems: 'stretch',
    position: 'relative',
    flexDirection: vertical ? 'column' : 'row'
  };
};

var clamp = function clamp(num, _clamp, higher) {
  return Math.min(Math.max(num, _clamp), higher);
};

function Slider(_ref) {
  var children = _ref.children,
      index = _ref.index,
      onIndexChange = _ref.onIndexChange,
      className = _ref.className,
      style = _ref.style,
      slideStyle = _ref.slideStyle,
      enabled = _ref.enabled,
      trail = _ref.trail,
      vertical = _ref.vertical,
      draggedScale = _ref.draggedScale,
      draggedSpring = _ref.draggedSpring,
      trailingSpring = _ref.trailingSpring,
      onDragStart = _ref.onDragStart,
      onDragEnd = _ref.onDragEnd;
  var slideStyleFunc = typeof slideStyle === 'function' ? slideStyle : function () {
    return slideStyle;
  };

  var _useResizeObserver = useResizeObserver(),
      _useResizeObserver2 = _slicedToArray(_useResizeObserver, 3),
      root = _useResizeObserver2[0],
      width = _useResizeObserver2[1],
      height = _useResizeObserver2[2];

  var axis = vertical ? 'y' : 'x';
  var size = vertical ? height : width;
  var indexRef = useRef(index);
  var restPos = useRef(0);
  var visibleIndexes = useRef(new Set());
  var firstVisibleIndex = useRef(0);
  var lastVisibleIndex = useRef(0);
  var instances = useRef();

  var cb = function cb(slides) {
    slides.forEach(function (_ref2) {
      var isIntersecting = _ref2.isIntersecting,
          target = _ref2.target;
      return visibleIndexes.current[isIntersecting ? 'add' : 'delete'](~~target.attributes['data-index'].value);
    });
    var visibles = Array.from(visibleIndexes.current).sort();
    firstVisibleIndex.current = visibles[0];
    lastVisibleIndex.current = visibles[visibles.length - 1];
  };

  var observer = useRef(null);
  if (!observer.current) observer.current = new IntersectionObserver(cb);
  useEffect(function () {
    Array.from(root.current.children).forEach(function (t) {
      return observer.current.observe(t);
    });
  }, [children.length, root]);
  useEffect(function () {
    return function () {
      return observer.current.disconnect();
    };
  }, []);

  var _useSprings = useSprings(children.length, function (i, ctrl) {
    if (i === 0) instances.current = [];
    instances.current.push(ctrl);
    return {
      x: vertical ? 0 : restPos.current,
      y: vertical ? restPos.current : 0,
      s: 1,
      zIndex: 0,
      immediate: function immediate(key) {
        return key === 'zIndex';
      }
    };
  }),
      _useSprings2 = _slicedToArray(_useSprings, 2),
      springs = _useSprings2[0],
      set = _useSprings2[1];

  useEffect(function () {
    if (vertical) {
      var _root$current$childre = root.current.children[index],
          offsetTop = _root$current$childre.offsetTop,
          offsetHeight = _root$current$childre.offsetHeight;
      restPos.current = -offsetTop + (height - offsetHeight) / 2;
    } else {
      var _root$current$childre2 = root.current.children[index],
          offsetLeft = _root$current$childre2.offsetLeft,
          offsetWidth = _root$current$childre2.offsetWidth;
      restPos.current = -offsetLeft + (width - offsetWidth) / 2;
    }

    if (indexRef.current === index) set(function () {
      var _ref3;

      return _ref3 = {}, _defineProperty(_ref3, axis, restPos.current), _defineProperty(_ref3, "s", 1), _defineProperty(_ref3, "config", trailingSpring), _ref3;
    });else {
      var dir = index < indexRef.current ? -1 : 1;
      var firstToMove = dir > 0 ? firstVisibleIndex.current : lastVisibleIndex.current;
      set(function (i) {
        var _ref4;

        var attachIdx = i < firstToMove ? i + 1 : i - 1;
        var attachController = instances.current[attachIdx];
        return _ref4 = {}, _defineProperty(_ref4, axis, restPos.current), _defineProperty(_ref4, "s", 1), _defineProperty(_ref4, "config", trailingSpring), _defineProperty(_ref4, "attach", trail && i !== firstToMove && !!attachController && function () {
          return attachController;
        }), _ref4;
      });
    }
    indexRef.current = index;
  }, [index, set, root, vertical, axis, height, width, trail, trailingSpring]);
  var bind = useDrag(function (_ref5) {
    var first = _ref5.first,
        last = _ref5.last,
        _ref5$vxvy = _slicedToArray(_ref5.vxvy, 2),
        vx = _ref5$vxvy[0],
        vy = _ref5$vxvy[1],
        _ref5$movement = _slicedToArray(_ref5.movement, 2),
        movX = _ref5$movement[0],
        movY = _ref5$movement[1],
        _ref5$args = _slicedToArray(_ref5.args, 1),
        pressedIndex = _ref5$args[0],
        _ref5$memo = _ref5.memo,
        memo = _ref5$memo === void 0 ? springs[pressedIndex][axis].getValue() : _ref5$memo;

    var v = vertical ? vy : vx;
    var delta = vertical ? movY : movX;

    if (first) {
      set(function (i) {
        var _ref6;

        var attachIdx = i === pressedIndex ? -1 : i < pressedIndex ? i + 1 : i - 1;
        var attachController = instances.current[attachIdx];
        return _ref6 = {}, _defineProperty(_ref6, axis, memo + delta), _defineProperty(_ref6, "s", draggedScale), _defineProperty(_ref6, "config", function config(key) {
          return key === axis && i === pressedIndex ? draggedSpring : trailingSpring;
        }), _defineProperty(_ref6, "zIndex", i === pressedIndex ? 10 : 0), _defineProperty(_ref6, "attach", trail && !!attachController && function () {
          return attachController;
        }), _ref6;
      });
      onDragStart && onDragStart(pressedIndex);
    } else if (last) {
      if (Math.abs(delta) > size / 2 || Math.abs(v) > 0.3) {
        indexRef.current = clamp(indexRef.current + (delta > 0 ? -1 : 1), 0, children.length - 1);
      }

      if (index !== indexRef.current) requestAnimationFrame(function () {
        return onIndexChange(indexRef.current);
      });else set(function () {
        var _ref7;

        return _ref7 = {}, _defineProperty(_ref7, axis, restPos.current), _defineProperty(_ref7, "s", 1), _defineProperty(_ref7, "config", trailingSpring), _ref7;
      });
      onDragEnd && onDragEnd(pressedIndex);
    } else set(function (i) {
      var _ref8;

      return _ref8 = {}, _defineProperty(_ref8, axis, memo + delta), _defineProperty(_ref8, "config", function config(key) {
        return key === axis && i === pressedIndex ? draggedSpring : trailingSpring;
      }), _ref8;
    });

    return memo;
  }, {
    enabled: enabled
  });
  return React.createElement("div", {
    className: className,
    style: style
  }, React.createElement("div", {
    ref: root,
    style: slidesWrapperStyle(vertical)
  }, springs.map(function (_ref9, i) {
    var _extends2;

    var pos = _ref9[axis],
        s = _ref9.s,
        zIndex = _ref9.zIndex;
    return React.createElement(animated.div, _extends({}, bind(i), {
      key: i,
      "data-index": i,
      style: _extends({}, slideStyleFunc(i), (_extends2 = {
        zIndex: zIndex
      }, _defineProperty(_extends2, axis, pos), _defineProperty(_extends2, "scale", s), _defineProperty(_extends2, "display", 'flex'), _defineProperty(_extends2, "alignItems", 'center'), _defineProperty(_extends2, "willChange", 'transform'), _extends2))
    }), children[i]);
  })));
}

Slider.defaultProps = {
  className: undefined,
  style: undefined,
  enabled: true,
  trail: true,
  vertical: false,
  draggedScale: 1,
  draggedSpring: {
    tension: 1200,
    friction: 40
  },
  trailingSpring: {
    mass: 10,
    tension: 800,
    friction: 200
  },
  onDragStart: undefined,
  onDragEnd: undefined
};
Slider.propTypes = {
  children: PropTypes.node.isRequired,
  index: PropTypes.number.isRequired,
  onIndexChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  slideStyle: PropTypes.object,
  enabled: PropTypes.bool,
  vertical: PropTypes.bool,
  trail: PropTypes.bool,
  draggedScale: PropTypes.number,
  draggedSpring: PropTypes.object,
  trailingSpring: PropTypes.object,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func
};
export { Slider };